import { DefaultMultiAutocomplete } from "../../../../../../common/components/default_multi_autocomplete";
import { DefaultSelectInput } from "../../../../../../common/components/default_select_input";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { FornecedorDropDown } from "../../../../../../common/domain/models/fornecedor_dropdown";
import { ProdutoServicoDropDown } from "../../../../../../common/domain/models/produto_servico_dropdown";
import { useMemo } from "react";
import { DefaultDateRangePickerInput } from "../../../../../../common/components/default_date_range_picker_input";
import { ColetaPrecoDropDown } from "../../../../../../common/domain/models/coleta_preco_dropdown";
import { OrdemCompraDropDown } from "../../../../../../common/domain/models/ordem_compra_dropdown";

import CascateList from "../../../../../../common/components/cascate_list";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DefaultInput from "../../../../../../common/components/default_input";

/**
 * Header
 */

type Props = {
  centroCusto: number[] | null;
  fornecedor: number[] | null;
  coletaPreco: number[] | null;
  ordemCompra: number[] | null;
  produtoServico: number[] | null;
  status: number[] | null;
  statusNF: number;
  nroNotaFiscal: string | null;
  dataOCStart: Date | null;
  dataOCEnd: Date | null;
  fornecedoresDropdown: FornecedorDropDown[];
  coletaPrecoDropdown: ColetaPrecoDropDown[];
  ordemComprasDropdown: OrdemCompraDropDown[];
  produtoServicosDropdown: ProdutoServicoDropDown[];
  centroCustoDropdown: CentroCustoDropDown[];
  onCentroCustoChange: (centroCusto: number[] | null) => void;
  onFornecedorChange: (fornecedor: number[] | null) => void;
  onColetaPrecoChange: (coletaPreco: number[] | null) => void;
  onOrdemCompraChange: (ordemCompra: number[] | null) => void;
  onProdutoServicoChange: (produtoServico: number[] | null) => void;
  onStatusChange: (status: number[] | null) => void;
  onStatusNFChange: (statusNF: number) => void;
  onNroNotaFiscalChange: (nroNotaFiscal: string | null) => void;
  onDataOCRChange: (de: Date | null, ate: Date | null) => void;
  onClearFilters: () => void;
};

export const Header = ({
  centroCusto,
  fornecedor,
  coletaPreco,
  ordemCompra,
  produtoServico,
  status,
  statusNF,
  nroNotaFiscal,
  dataOCStart,
  dataOCEnd,
  fornecedoresDropdown,
  coletaPrecoDropdown,
  ordemComprasDropdown,
  produtoServicosDropdown,
  centroCustoDropdown,
  onCentroCustoChange,
  onFornecedorChange,
  onColetaPrecoChange,
  onOrdemCompraChange,
  onProdutoServicoChange,
  onStatusChange,
  onStatusNFChange,
  onNroNotaFiscalChange,
  onDataOCRChange,
  onClearFilters,
}: Props) => {
  const statusOptions = useMemo(() => {
    return [
      {
        id: 2,
        label: 'Pendentes',
      },
      {
        id: 3,
        label: 'Entregue totalmente',
      },
      {
        id: 4,
        label: 'Entrega Parcial',
      },
      {
        id: 5,
        label: 'Canceladas',
      },
      {
        id: 6,
        label: 'Atrasadas',
      },
    ];
  }, [
  ]);

  const statusNFOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Todas',
      },
      {
        id: 2,
        label: 'Sim',
      },
      {
        id: 3,
        label: 'Não',
      },
    ];
  }, [
  ]);

  const selectedCentroCusto = useMemo(() => {
    return centroCustoDropdown.filter(x => centroCusto?.some(y => y === x.id)) ?? null;
  }, [
    centroCusto,
    centroCustoDropdown,
  ]);

  const selectedFornecedor = useMemo(() => {
    return fornecedoresDropdown.filter(x => fornecedor?.some(y => y === x.idFornecedor)) ?? null;
  }, [
    fornecedor,
    fornecedoresDropdown,
  ]);

  const selectedProdutoServico = useMemo(() => {
    return produtoServicosDropdown.filter(x => produtoServico?.some(y => y === x.idProdutoServico)) ?? null;
  }, [
    produtoServico,
    produtoServicosDropdown,
  ]);

  const selectedColetaPreco = useMemo(() => {
    return coletaPrecoDropdown.filter(x => coletaPreco?.some(y => y === x.idColetaPreco)) ?? null;
  }, [
    coletaPreco,
    coletaPrecoDropdown,
  ]);

  const selectedOrdemCompra = useMemo(() => {
    return ordemComprasDropdown.filter(x => ordemCompra?.some(y => y === x.idOrdemCompra)) ?? null;
  }, [
    ordemCompra,
    ordemComprasDropdown,
  ]);

  const selectedStatus = useMemo(() => {
    return statusOptions.filter(x => status?.some(y => y === x.id)) ?? null;
  }, [
    status,
    statusOptions,
  ]);

  return (
    <CascateList title='Filtros' defaultOpen={true}>
      <div className="w-full flex flex-col gap-4">
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          <DefaultMultiAutocomplete
            label='Centro de Custo'
            options={centroCustoDropdown}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.title}
            value={selectedCentroCusto}
            onChange={e => onCentroCustoChange(e !== null ? e.map(x => x.id) : null)}
          />

          <DefaultMultiAutocomplete
            label='Fornecedor'
            options={fornecedoresDropdown}
            getOptionKey={x => x.idFornecedor}
            getOptionLabel={x => x.nomeFantasia}
            value={selectedFornecedor}
            onChange={e => onFornecedorChange(e !== null ? e.map(x => x.idFornecedor) : null)}
          />

          <DefaultMultiAutocomplete
            label='Coleta Preço'
            options={coletaPrecoDropdown}
            getOptionKey={x => x.idColetaPreco}
            getOptionLabel={x => x.descricao}
            value={selectedColetaPreco}
            onChange={e => onColetaPrecoChange(e !== null ? e.map(x => x.idColetaPreco) : null)}
          />

          <DefaultMultiAutocomplete
            label='Ordem Compra'
            options={ordemComprasDropdown}
            getOptionKey={x => x.idOrdemCompra}
            getOptionLabel={x => x.descricao}
            value={selectedOrdemCompra}
            onChange={e => onOrdemCompraChange(e !== null ? e.map(x => x.idOrdemCompra) : null)}
          />

          <DefaultMultiAutocomplete
            label='Produto/Serviço'
            options={produtoServicosDropdown}
            getOptionKey={x => x.idProdutoServico}
            getOptionLabel={x => x.descricao}
            value={selectedProdutoServico}
            onChange={e => onProdutoServicoChange(e !== null ? e.map(x => x.idProdutoServico) : null)}
          />

          <DefaultMultiAutocomplete
            label='Status'
            options={statusOptions}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.label}
            value={selectedStatus}
            onChange={e => onStatusChange(e !== null ? e.map(x => x.id) : null)}
          />

          <DefaultSelectInput
            name='statusNF'
            label='NF Entregue'
            showSelectionLabel={false}
            options={statusNFOptions}
            value={statusNF}
            onChangeValue={e => onStatusNFChange(Number(e))}
          />

          <DefaultInput
            name='nroNotaFiscal'
            label='N° da Nota'
            value={nroNotaFiscal}
            onChangeValue={e => onNroNotaFiscalChange(e !== null && e.length > 0 ? e : null)}
          />

          <DefaultDateRangePickerInput
            label='Data da OC'
            dataDe={dataOCStart}
            dataAte={dataOCEnd}
            onChange={(de, ate) => onDataOCRChange(de, ate)}
          />
        </div>

        <div className="w-full flex justify-end">
          <div className="w-[180px]">
            <PrimaryButton
              startIcon={<DynamicIcon nameIcon="ClearRounded" size={25} className="text-black" />}
              buttonColor="yellow"
              onClick={onClearFilters}>
              Limpar filtros
            </PrimaryButton>
          </div>
        </div>
      </div>
    </CascateList>
  );
}